import React from "react";
import { authorizedFetch } from "./boot.js";

const { useEffect, useState } = React;
const clock = value => new Intl.DateTimeFormat("en-AU", { timeZone: "Australia/Sydney", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(value));
const statuses = { recording: "Recording", historical: "Recorded day", paused: "Counting paused", offline: "Camera count unavailable", stale: "Updates delayed", unavailable: "Count status unavailable", no_data: "No recorded counts" };

export function useParadeActivity(sale, enabled, allowHistory = false) {
  const [day, setDay] = useState("");
  const [state, setState] = useState({ data: null, error: "" });
  const [attempt, retry] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let timer, pending = false;
    setState({ data: null, error: "" });
    const refresh = async () => {
      clearTimeout(timer);
      if (document.hidden || pending || controller.signal.aborted) return;
      pending = true;
      try {
        const response = await authorizedFetch(`/collections/live.parade_activity@1/?${new URLSearchParams({ sale, ...(allowHistory && day ? { day } : {}) })}`, { signal: controller.signal });
        if (!response.ok) throw new Error(response.status === 404 ? "Activity is not available for this sale or your access has changed." : response.status === 401 ? "Sign in again to view activity." : "Activity could not be refreshed. Please retry.");
        const data = await response.json();
        if (!Array.isArray(data.items)) throw new Error("Activity could not be loaded.");
        if (!controller.signal.aborted) setState({ data, error: "" });
      } catch (error) {
        // Discard previous realm/date data and revoked results, including peaks.
        if (!controller.signal.aborted) setState({ data: null, error: error.message });
      } finally {
        pending = false;
        if (!controller.signal.aborted && !document.hidden) timer = setTimeout(refresh, 30000);
      }
    };
    const visibility = () => { clearTimeout(timer); if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", visibility);
    refresh();
    return () => { controller.abort(); clearTimeout(timer); document.removeEventListener("visibilitychange", visibility); };
  }, [sale, day, enabled, allowHistory, attempt]);
  return { ...state, day, setDay, allowHistory, retry: () => retry(n => n + 1) };
}

export function ActivityDate({ activity }) {
  return <div className="activity-toolbar">
    <div><strong>Parade activity</strong><p>Estimated people and horses visible · counts recorded 08:00–18:00 Sydney time</p></div>
    {activity.allowHistory && activity.data ? <label>Day <input type="date" aria-label="Parade activity day" value={activity.day || activity.data.day} min={activity.data.minDay} max={activity.data.maxDay} onChange={event => event.target.value && activity.setDay(event.target.value)}/></label> : !activity.allowHistory && <span>Today · Sydney time</span>}
    {activity.allowHistory && activity.day && <button type="button" className="btn" onClick={() => activity.setDay("")}>Today</button>}
    {activity.error && <p role="alert">{activity.error} <button type="button" className="btn" onClick={activity.retry}>Retry activity</button></p>}
  </div>;
}

export function chartSegments(points, metric, max) {
  const segments = [];
  let segment = [];
  points.forEach((point, i) => {
    const value = point[metric];
    if (value === null || !point.samples) {
      if (segment.length) segments.push(segment);
      segment = [];
    } else segment.push([36 + i * 456 / Math.max(1, points.length), 128 - value * 104 / max, point]);
  });
  if (segment.length) segments.push(segment);
  return segments;
}

function Peak({ name, peak }) {
  return <div><span>{name}</span><strong>{peak ? peak.value : "—"}</strong><small>{peak ? `${clock(peak.at)}–${clock(peak.until)}` : "No observations"}</small></div>;
}

export function ParadeActivity({ camera, activity }) {
  const [people, setPeople] = useState(true);
  const [horses, setHorses] = useState(true);
  const [details, setDetails] = useState(false);
  const row = activity.data?.items.find(item => item.camera === camera.id);
  const points = row?.points || [];
  const observed = points.filter(p => p.samples > 0);
  const metrics = [...(people ? ["people"] : []), ...(horses ? ["horses"] : [])];
  const max = Math.max(1, ...observed.flatMap(p => metrics.map(metric => p[metric] ?? 0)));
  return <div className="parade-activity" aria-label={`${camera.label} activity`}>
    {!row ? <p role="status">{activity.error ? "Activity unavailable" : activity.data ? "No activity access for this area" : "Loading activity…"}</p> : <>
      <div className="activity-heading"><strong>Day’s activity</strong><span>{statuses[row.status] || "Status unavailable"}</span></div>
      <div className="activity-peaks"><Peak name="Peak people" peak={row.peaks.people}/><Peak name="Peak horses" peak={row.peaks.horses}/></div>
      <div className="activity-legend"><button type="button" className="activity-people" aria-pressed={people} onClick={() => setPeople(v => !v)}>People</button><button type="button" className="activity-horses" aria-pressed={horses} onClick={() => setHorses(v => !v)}>Horses</button><span>Peak observed per 5 minutes</span></div>
      {observed.length ? <svg className="activity-chart" viewBox="0 0 510 157" role="img" aria-label={`${camera.label}: people and horses visible through the day. Peak people ${row.peaks.people?.value ?? "unavailable"}; peak horses ${row.peaks.horses?.value ?? "unavailable"}.`}>
        <title>{camera.label} — {activity.data.day} parade activity</title>
        {[0, max / 2, max].map((value, i) => <g key={i}><line x1="36" x2="492" y1={128 - value * 104 / max} y2={128 - value * 104 / max} className="activity-gridline"/><text x="29" y={132 - value * 104 / max} textAnchor="end">{Number.isInteger(value) ? value : value.toFixed(1)}</text></g>)}
        {metrics.map(metric => <g key={metric} className={`activity-${metric}`}>{chartSegments(points, metric, max).map((segment, i) => <g key={i}>
          <polyline points={segment.map(([x, y]) => `${x},${y}`).join(" ")} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke"/>
          {segment.map(([x, y, p]) => <circle key={p.at} cx={x} cy={y} r={p.samples < 8 ? 2.7 : 1.7} fill="currentColor"><title>{clock(p.at)} · {metric === "people" ? "People" : "Horses"}: {p[metric]} · {p.samples} samples{p.samples < 8 ? " (partial interval)" : ""}</title></circle>)}
        </g>)}</g>)}
        <text x="36" y="151">08:00</text><text x="264" y="151" textAnchor="middle">13:00</text><text x="492" y="151" textAnchor="end">18:00</text>
      </svg> : <p className="activity-empty">No observations recorded for this day.</p>}
      <div className="activity-footnote"><span>{row.lastObserved ? `Observed ${clock(row.lastObserved)}` : row.lastUpdated ? `Data updated ${clock(row.lastUpdated)}` : "No data timestamp"} · Sydney time</span><button type="button" onClick={() => setDetails(v => !v)} aria-expanded={details}> {details ? "Hide readings" : "View readings"}</button></div>
      {details && <div className="activity-readings" tabIndex="0"><table><caption>{camera.label} five-minute observations · {activity.data.day}</caption><thead><tr><th>Time</th><th>People</th><th>Horses</th><th>Samples</th></tr></thead><tbody>{points.map(p => <tr key={p.at}><th>{clock(p.at)}</th><td>{p.people ?? "—"}</td><td>{p.horses ?? "—"}</td><td>{p.samples || "—"}</td></tr>)}</tbody></table></div>}
      <p className="activity-note">Estimated counts, not unique visitors or total parades. Gaps mean no readings; larger dots mark partial intervals.</p>
    </>}
  </div>;
}
